/**
 * Message Templates Types
 * Type definitions for the Templates feature
 */

import type { Id } from '../../convex/_generated/dataModel';
import type {
  TemplateCategory,
  TemplateChannel,
  TemplateStatus,
  TemplateTab,
  TemplateSortOption,
  TemplateViewMode,
} from '@/constants/templateConstants';

// ============================================
// Core Template Types
// ============================================

export interface IMessageTemplate {
  _id: Id<'messageTemplates'>;
  userId: Id<'users'>;
  businessId: Id<'businesses'>;
  name: string;
  content: string;
  category: TemplateCategory;
  channels: string[];
  status: TemplateStatus;
  isShareable: boolean;
  sharedWithBusinessIds: Id<'businesses'>[];
  usageCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface IMessageTemplateCreate {
  businessId: Id<'businesses'>;
  name: string;
  content: string;
  category: TemplateCategory;
  channels: TemplateChannel[];
  status: TemplateStatus;
  isShareable: boolean;
  sharedWithBusinessIds: Id<'businesses'>[];
}

export interface IMessageTemplateUpdate {
  templateId: Id<'messageTemplates'>;
  name?: string;
  content?: string;
  category?: TemplateCategory;
  channels?: TemplateChannel[];
  status?: TemplateStatus;
  isShareable?: boolean;
  sharedWithBusinessIds?: Id<'businesses'>[];
}

// ============================================
// Business Types for Sharing
// ============================================

export interface IShareableBusiness {
  _id: Id<'businesses'>;
  name: string;
  isOwner: boolean;
}

// ============================================
// Filter & Sort Types
// ============================================

export interface ITemplateFilters {
  tab: TemplateTab;
  channel: TemplateChannel | 'all';
  category: TemplateCategory | 'all';
  sort: TemplateSortOption;
  search: string;
}

export const DEFAULT_TEMPLATE_FILTERS: ITemplateFilters = {
  tab: 'my_templates',
  channel: 'all',
  category: 'all',
  sort: 'newest',
  search: '',
};

// ============================================
// Component Props Types
// ============================================

export interface ITemplateCardProps {
  template: IMessageTemplate;
  isOwner: boolean;
  onEdit: (template: IMessageTemplate) => void;
  onDelete: (templateId: Id<'messageTemplates'>) => void;
  onCopy: (templateId: Id<'messageTemplates'>) => void;
}

export interface ITemplateTableProps {
  templates: IMessageTemplate[];
  isOwner: boolean;
  onEdit: (template: IMessageTemplate) => void;
  onDelete: (templateId: Id<'messageTemplates'>) => void;
  onCopy: (templateId: Id<'messageTemplates'>) => void;
}

export interface ITemplateTableRowProps {
  template: IMessageTemplate;
  isOwner: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
}

export interface ITemplateEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: IMessageTemplate;
  businessId: Id<'businesses'>;
  userBusinesses: IShareableBusiness[];
  onSave: (data: IMessageTemplateCreate | IMessageTemplateUpdate) => Promise<void>;
  onDelete?: (templateId: Id<'messageTemplates'>) => Promise<void>;
}

export interface ITemplateFiltersProps {
  filters: ITemplateFilters;
  onFiltersChange: (filters: ITemplateFilters) => void;
  templateCount: number;
}

export interface IVariableChipsProps {
  onVariableClick: (variable: string) => void;
}

export interface ITemplateViewToggleProps {
  viewMode: TemplateViewMode;
  onViewModeChange: (mode: TemplateViewMode) => void;
}

// ============================================
// Hook Return Types
// ============================================

export interface IUseMessageTemplatesReturn {
  templates: IMessageTemplate[];
  userBusinesses: IShareableBusiness[];
  isLoading: boolean;
  error: string | null;
  createTemplate: (data: IMessageTemplateCreate) => Promise<Id<'messageTemplates'>>;
  updateTemplate: (data: IMessageTemplateUpdate) => Promise<void>;
  deleteTemplate: (templateId: Id<'messageTemplates'>) => Promise<void>;
  copyTemplate: (templateId: Id<'messageTemplates'>) => Promise<Id<'messageTemplates'>>;
  incrementUsage: (templateId: Id<'messageTemplates'>) => Promise<void>;
}

// ============================================
// Form State Types
// ============================================

export interface ITemplateFormState {
  name: string;
  content: string;
  category: TemplateCategory;
  channels: TemplateChannel[];
  status: TemplateStatus;
  isShareable: boolean;
  sharedWithBusinessIds: Id<'businesses'>[];
}

const DEFAULT_TEMPLATE_FORM_STATE: ITemplateFormState = {
  name: '',
  content: '',
  category: 'general',
  channels: [],
  status: 'draft',
  isShareable: false,
  sharedWithBusinessIds: [],
};

export function getInitialFormState(template?: IMessageTemplate): ITemplateFormState {
  if (template) {
    return {
      name: template.name,
      content: template.content,
      category: template.category,
      channels: template.channels as TemplateChannel[],
      status: template.status,
      isShareable: template.isShareable,
      sharedWithBusinessIds: template.sharedWithBusinessIds,
    };
  }
  return DEFAULT_TEMPLATE_FORM_STATE;
}
